import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from '../../../ui/core';
import { useFormatters } from '../../../hooks';
import { formatBonusTripLabel } from '../../../helpers/utils/massBonus';
import ResponsiveSheet from '../../shared/ResponsiveSheet';

// The first half of the mass-bonus flow: pick which of the on-screen Needs
// Attention trips to bonus. Narrow with the filters first, then Select all.
// Continue hands the picks to MassBonusDialog for the amount.
//
// `canOverride` (full ADMINs) adds a toggle that widens `trips` to every trip on
// screen, whatever its status; each row then names its status, since the list no
// longer implies it.
const MassBonusPicker = ({
  open,
  onClose,
  trips,
  selectedIds,
  onChange,
  onContinue,
  canOverride = false,
  anyStatus = false,
  onAnyStatusChange,
}) => {
  const f = useFormatters();
  const selectedCount = trips.filter((t) => selectedIds.includes(t.id)).length;
  const allSelected = selectedCount === trips.length && trips.length > 0;

  const toggle = (id) =>
    onChange(selectedIds.includes(id) ? selectedIds.filter((i) => i !== id) : [...selectedIds, id]);

  return (
    <ResponsiveSheet open={open} onClose={onClose}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Box sx={{ flex: 1 }}>Mass bonus</Box>
        <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
          {`${selectedCount} of ${trips.length} selected`}
        </Typography>
        <Button size="small" onClick={() => onChange(allSelected ? [] : trips.map((t) => t.id))}>
          {allSelected ? 'Clear' : 'Select all'}
        </Button>
      </DialogTitle>
      {canOverride && (
        <Box sx={{ px: '24px', pb: '8px' }}>
          <FormControlLabel
            sx={{ mr: 0 }}
            control={<Checkbox checked={anyStatus} onChange={(e) => onAnyStatusChange(e.target.checked)} />}
            label={
              <Typography sx={{ fontSize: '14px' }}>
                Include trips outside Needs Attention
                <Typography component="span" sx={{ fontSize: '12px', color: 'text.secondary', ml: '6px' }}>
                  Admin override
                </Typography>
              </Typography>
            }
          />
        </Box>
      )}
      <DialogContent dividers sx={{ px: '16px' }}>
        <Stack>
          {!trips.length && (
            <Typography sx={{ fontSize: '14px', color: 'text.secondary', py: '8px' }}>
              No trips in Needs Attention.
            </Typography>
          )}
          {trips.map((t) => {
            const bonus = t.payoutItemized?.manualBonus || 0;
            return (
              <FormControlLabel
                key={t.id}
                sx={{ mr: 0, py: '2px' }}
                control={<Checkbox checked={selectedIds.includes(t.id)} onChange={() => toggle(t.id)} />}
                label={
                  <Box>
                    <Typography sx={{ fontSize: '14px' }}>{formatBonusTripLabel(f, t)}</Typography>
                    {(anyStatus || bonus > 0) && (
                      <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                        {[anyStatus && t.displayStatus, bonus > 0 && `Manual bonus ${f.number.currency(bonus)}`]
                          .filter(Boolean)
                          .join(' · ')}
                      </Typography>
                    )}
                  </Box>
                }
              />
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="error" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" disabled={!selectedCount} onClick={onContinue}>
          Continue
        </Button>
      </DialogActions>
    </ResponsiveSheet>
  );
};

export default MassBonusPicker;
